import {
  Component,
  Input,
  OnChanges,
  OnInit,
  Output,
  EventEmitter,
  SimpleChanges
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  Alignment,
  AutoLink,
  Autosave,
  BlockQuote,
  Bold,
  ClassicEditor,
  EditorConfig,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  Heading,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  Paragraph,
  SourceEditing,
  Underline
} from 'ckeditor5';
import { Subscription, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { ElementSlideDownAnimation } from 'src/app/shared/animations/element-slide-down.animation';
import { ItemPickerModel } from 'src/app/shared/components/item-picker/models/itemPickerModel';
import { AttributeDefinition } from 'src/app/shared/models/attributes/attributeDefinition';
import { AttributeUiType } from 'src/app/shared/models/attributes/attributeUiType';

@Component({
  selector: 'rich-text-editor',
  templateUrl: 'rich-text-editor.component.html',
  styleUrls: ['rich-text-editor.component.scss'],
  animations: [ElementSlideDownAnimation()],
  standalone: false
})
export class RichTextEditorComponent implements OnInit, OnChanges {
  editor = ClassicEditor;
  private editorInstance: ClassicEditor;
  capitalizedField: string;
  isFocused = false;
  show = false;
  mergeTagControl = new UntypedFormControl();
  mergeTagGroups: MergeTagGroup[] = [];
  filteredMergeTagGroups: Observable<MergeTagGroup[]>;
  selectedMergeTag: ItemPickerModel = null;
  credentialControl = new UntypedFormControl();
  credentialFieldControl = new UntypedFormControl();
  credentialItems: ItemPickerModel[] = [];
  filteredCredentialItems: Observable<ItemPickerModel[]>;
  filteredCredentialFields: Observable<ItemPickerModel[]>;
  selectedCredentialDefinition: ItemPickerModel = null;
  selectedCredentialField: ItemPickerModel = null;
  private credentialFieldsByDefinitionId: Record<string, ItemPickerModel[]> = {};

  private formValueChanges: Subscription;

  @Output()
  blur = new EventEmitter<string>();

  @Output()
  textChanged = new EventEmitter<string>();

  @Input()
  field: string;

  @Input()
  fieldName: string = '';

  @Input()
  form: any;

  @Input()
  label: string;

  @Input()
  attributes: AttributeDefinition[] = [];

  @Input()
  getFocusWhenStart = false;

  @Input()
  placeholder: string = '';

  @Input()
  includeTeamData = false;

  @Input()
  includePersonData = false;

  @Input() includeCredentialData = false;

  @Input() includeCredentialApplicationData = false;

  @Input() credentialDefinitions: { id: string; name: string }[] = [];

  @Input() readOnly = false;

  @Input()
  clearData = false;

  config: EditorConfig = {
    toolbar: {
      items: [
        'heading',
        '|',
        'bold',
        'italic',
        'underline',
        '|',
        'fontSize',
        'fontFamily',
        'fontColor',
        'fontBackgroundColor',
        '|',
        'alignment',
        'numberedList',
        'bulletedList',
        'outdent',
        'indent',
        '|',
        'blockQuote',
        'link',
        '|',
        'sourceEditing'
      ],
      shouldNotGroupWhenFull: false
    },
    plugins: [
      Alignment,
      AutoLink,
      Autosave,
      BlockQuote,
      Bold,
      Essentials,
      FontBackgroundColor,
      FontColor,
      FontFamily,
      FontSize,
      Heading,
      Indent,
      IndentBlock,
      Italic,
      Link,
      List,
      Paragraph,
      SourceEditing,
      Underline
    ],
    fontFamily: {
      supportAllValues: true
    },
    fontSize: {
      options: [10, 12, 14, 'default', 18, 20, 22],
      supportAllValues: true
    },
    heading: {
      options: [
        {
          model: 'paragraph',
          title: 'Paragraph',
          class: 'ck-heading_paragraph'
        },
        {
          model: 'heading1',
          view: 'h1',
          title: 'Heading 1',
          class: 'ck-heading_heading1'
        },
        {
          model: 'heading2',
          view: 'h2',
          title: 'Heading 2',
          class: 'ck-heading_heading2'
        },
        {
          model: 'heading3',
          view: 'h3',
          title: 'Heading 3',
          class: 'ck-heading_heading3'
        },
        {
          model: 'heading4',
          view: 'h4',
          title: 'Heading 4',
          class: 'ck-heading_heading4'
        },
        {
          model: 'heading5',
          view: 'h5',
          title: 'Heading 5',
          class: 'ck-heading_heading5'
        },
        {
          model: 'heading6',
          view: 'h6',
          title: 'Heading 6',
          class: 'ck-heading_heading6'
        }
      ]
    },
    licenseKey: 'GPL',
    placeholder: 'Type or paste your content here!'
  };

  private _data: string = '';
  get data(): string {
    return this._data ? this._data : '';
  }
  set data(value: string) {
    if (value && typeof value === 'string')
      this._data = value.replace(/(^[ \t]*\n)/gm, '<br/><br/>');
  }

  ngOnInit(): void {
    this.config.placeholder = this.placeholder;
    this.data = this.form.get(this.field).value;
    this.show = true; //to render text editor after placeholder gets value
    this.capitalizedField = this.capitalize(this.fieldName ? this.fieldName : this.field);

    this.formValueChanges = this.form.valueChanges.subscribe(() => {
      const newValue = this.form.get(this.field).value;
      if (this.data != newValue) {
        this.data = newValue;
      }
    });
    if (
      (this.attributes && this.attributes.length > 0) ||
      this.includePersonData ||
      this.includeTeamData ||
      this.includeCredentialData
    ) {
      this.buildMergeTagGroups();
      this.setupFilteredGroups();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !this.show ||
      (!changes['includePersonData'] &&
        !changes['includeTeamData'] &&
        !changes['includeCredentialData'] &&
        !changes['includeCredentialApplicationData'] &&
        !changes['credentialDefinitions'] &&
        !changes['attributes'])
    ) {
      return;
    }
    this.buildMergeTagGroups();
    this.setupFilteredGroups();
  }

  private buildMergeTagGroups(): void {
    this.mergeTagGroups = [];
    if (this.includePersonData) {
      this.mergeTagGroups.push({ name: 'Person', items: this.createPersonItemPickerList() });
    }
    if (this.includeTeamData) {
      this.mergeTagGroups.push({ name: 'Team', items: this.createTeamItemPickerList() });
    }
    if (this.includeCredentialData && this.credentialDefinitions?.length > 0) {
      this.buildCredentialPickers();
    } else if (this.includeCredentialData) {
      this.mergeTagGroups.push({
        name: 'Credential',
        items: this.createReportCredentialItemPickerList()
      });
    }
    const customFieldTypes = ['Person', 'Team', 'Seat'];
    customFieldTypes.forEach((type) => {
      const items = this.createItemPickerList(type);
      if (items.length > 0) {
        this.mergeTagGroups.push({ name: `${type} Custom Fields`, items });
      }
    });
  }

  private buildCredentialPickers(): void {
    this.credentialItems = this.credentialDefinitions.map(
      (credentialDefinition) =>
        new ItemPickerModel(credentialDefinition.id, '', credentialDefinition.name)
    );
    this.credentialFieldsByDefinitionId = {};
    this.credentialDefinitions.forEach((credentialDefinition) => {
      this.credentialFieldsByDefinitionId[credentialDefinition.id] =
        this.createCredentialItemPickerList(credentialDefinition.id);
    });

    this.selectedCredentialDefinition = null;
    this.selectedCredentialField = null;
    this.credentialControl.setValue('');
    this.credentialFieldControl.setValue('');

    this.filteredCredentialItems = this.credentialControl.valueChanges.pipe(
      startWith(''),
      map((value) => (typeof value === 'string' ? value : value?.name || '')),
      map((search) => this.filterItems(this.credentialItems, search))
    );
    this.filteredCredentialFields = this.credentialFieldControl.valueChanges.pipe(
      startWith(''),
      map((value) => (typeof value === 'string' ? value : value?.name || '')),
      map((search) =>
        this.filterItems(
          this.selectedCredentialDefinition
            ? this.credentialFieldsByDefinitionId[this.selectedCredentialDefinition.id] || []
            : [],
          search
        )
      )
    );
  }

  private filterItems(items: ItemPickerModel[], search: string): ItemPickerModel[] {
    if (!search) {
      return items.slice();
    }
    const lower = search.toLowerCase();
    return items.filter((item) => item.name.toLowerCase().includes(lower));
  }

  onCredentialDefinitionSelected(credentialDefinition: ItemPickerModel): void {
    this.selectedCredentialDefinition = credentialDefinition;
    this.selectedCredentialField = null;
    this.credentialFieldControl.setValue('');
  }

  onCredentialFieldSelected(field: ItemPickerModel): void {
    this.selectedCredentialField = field;
    this.selectedMergeTag = field;
    this.copyToClipboardAndFocus(field);
  }

  private setupFilteredGroups(): void {
    this.filteredMergeTagGroups = this.mergeTagControl.valueChanges.pipe(
      startWith(''),
      map((value) => (typeof value === 'string' ? value : value?.name || '')),
      map((search) => (search ? this.filterGroups(search) : this.mergeTagGroups.slice()))
    );
  }

  private filterGroups(search: string): MergeTagGroup[] {
    const lower = search.toLowerCase();
    return this.mergeTagGroups
      .map((group) => ({
        name: group.name,
        items: group.items.filter((item) => item.name.toLowerCase().includes(lower))
      }))
      .filter((group) => group.items.length > 0);
  }

  displayMergeTag(tag: ItemPickerModel): string {
    return tag && tag.name ? tag.name : '';
  }

  private createItemPickerList(type: string) {
    var itemPickerList = [];
    const attributeItems = this.attributes
      .filter((a) => a.entityType === type)
      .map((f) => new ItemPickerModel(f.id, '', f.slug));
    itemPickerList.push(...attributeItems);
    return itemPickerList;
  }
  private createTeamItemPickerList() {
    var itemPickerList = [];
    itemPickerList.push(new ItemPickerModel('team.name', '', 'Team Name'));
    itemPickerList.push(
      new ItemPickerModel('dateFormat team.startDate "yyyy-MM-dd"', '', 'Team Start Date')
    );
    itemPickerList.push(
      new ItemPickerModel('dateFormat team.endDate "yyyy-MM-dd"', '', 'Team End Date')
    );
    itemPickerList.push(new ItemPickerModel('position.title', '', 'Position Title'));
    itemPickerList.push(new ItemPickerModel('position.description', '', 'Position Description'));
    return itemPickerList;
  }

  private createPersonItemPickerList() {
    var itemPickerList = [];
    itemPickerList.push(new ItemPickerModel('person.prefix', '', 'Person Prefix'));
    itemPickerList.push(new ItemPickerModel('person.firstName', '', 'Person First Name'));
    itemPickerList.push(new ItemPickerModel('person.lastName', '', 'Person Last Name'));
    itemPickerList.push(new ItemPickerModel('person.suffix', '', 'Person Suffix'));
    itemPickerList.push(new ItemPickerModel('person.email', '', 'Person Email'));
    itemPickerList.push(new ItemPickerModel('person.phoneNumber', '', 'Phone Number'));
    itemPickerList.push(new ItemPickerModel('personAddress.mailingAddress', '', 'Mailing Address'));
    itemPickerList.push(new ItemPickerModel('personAddress.city', '', 'City'));
    itemPickerList.push(new ItemPickerModel('personAddress.state', '', 'State'));
    return itemPickerList;
  }

  private createReportCredentialItemPickerList(): ItemPickerModel[] {
    const items = [
      new ItemPickerModel('credential.name', '', 'Credential Name'),
      new ItemPickerModel('credential.type', '', 'Credential Type'),
      new ItemPickerModel('credential.number', '', 'Credential Number'),
      new ItemPickerModel('credential.status', '', 'Credential Status'),
      new ItemPickerModel(
        'dateFormat credential.issueDate "yyyy-MM-dd"',
        '',
        'Credential Issue Date'
      ),
      new ItemPickerModel(
        'dateFormat credential.expirationDate "yyyy-MM-dd"',
        '',
        'Credential Expiration Date'
      ),
      new ItemPickerModel(
        'dateFormat credentialApplication.startDate "yyyy-MM-dd"',
        '',
        'Application Start Date'
      )
    ];
    if (this.includeCredentialApplicationData) {
      items.push(
        new ItemPickerModel('credentialApplication.type', '', 'Application Type'),
        new ItemPickerModel('credentialApplication.status', '', 'Application Status'),
        new ItemPickerModel('credentialApplication.statusLabel', '', 'Application Status Label')
      );
    }
    return items;
  }

  private createCredentialItemPickerList(credentialDefinitionId: string): ItemPickerModel[] {
    const items = [
      new ItemPickerModel(
        `personCredential.[${credentialDefinitionId}].number`,
        '',
        'Credential Number'
      ),
      new ItemPickerModel(
        `personCredential.[${credentialDefinitionId}].status`,
        '',
        'Credential Status'
      ),
      new ItemPickerModel(
        `dateFormat personCredential.[${credentialDefinitionId}].issueDate "yyyy-MM-dd"`,
        '',
        'Credential Issue Date'
      ),
      new ItemPickerModel(
        `dateFormat personCredential.[${credentialDefinitionId}].expirationDate "yyyy-MM-dd"`,
        '',
        'Credential Expiration Date'
      )
    ];
    if (this.includeCredentialApplicationData) {
      items.push(
        new ItemPickerModel(
          `personCredentialApplication.[${credentialDefinitionId}].type`,
          '',
          'Application Type'
        ),
        new ItemPickerModel(
          `personCredentialApplication.[${credentialDefinitionId}].status`,
          '',
          'Application Status'
        ),
        new ItemPickerModel(
          `personCredentialApplication.[${credentialDefinitionId}].statusLabel`,
          '',
          'Application Status Label'
        ),
        new ItemPickerModel(
          `dateFormat personCredentialApplication.[${credentialDefinitionId}].startDate "yyyy-MM-dd"`,
          '',
          'Application Start Date'
        )
      );
    }
    return items;
  }

  onMergeTagSelected(tag: ItemPickerModel): void {
    this.selectedMergeTag = tag;
    this.copyToClipboardAndFocus(tag);
  }

  copySelectedMergeTag(): void {
    if (this.selectedMergeTag) {
      this.copyToClipboardAndFocus(this.selectedMergeTag);
    }
  }
  private getAttributeById(id: string) {
    return this.attributes.find((attr) => attr.id === id);
  }
  private buildCustomFieldTemplate(attrId: string, displayName: string): string {
    const attr = this.getAttributeById(attrId);
    if (attr.subFieldList?.length === 0) {
      // Fallback simple field
      return `{{#customField "${displayName}"}}
  {{field.value}}
{{/customField}}`;
    }

    // Split subfields by uiType
    const regularSubs = attr.subFieldList.filter(
      (s: any) =>
        s.subFieldDefinition &&
        s.subFieldDefinition.uiType !== AttributeUiType.MultiSelect &&
        s.subFieldDefinition.uiType !== AttributeUiType.Checkboxes
    );

    const listSubs = attr.subFieldList.filter(
      (s: any) =>
        s.subFieldDefinition &&
        (s.subFieldDefinition.uiType === AttributeUiType.MultiSelect ||
          s.subFieldDefinition.uiType === AttributeUiType.Checkboxes)
    );

    let inner = '';

    // Plain text only: tags typed or pasted as text into the rich-text editor are escaped,
    // and the editor turns each line into a line break or paragraph itself.
    regularSubs.forEach((s: any) => {
      const def = s.subFieldDefinition;
      inner += `${def.name}: {{field.${def.slug}}}
`;
    });

    // MultiSelect / Checkbox: values joined on one line
    listSubs.forEach((s: any) => {
      const def = s.subFieldDefinition;
      inner += `${def.name}: {{#each field.${def.slug}}}{{this}}, {{/each}}
`;
    });

    if (!inner) {
      inner = '{{field.value}}\n';
    }

    return `{{#customField "${displayName}"}}
${inner}{{/customField}}`;
  }
  private copyToClipboardAndFocus(tag: ItemPickerModel): void {
    const name = tag?.name;
    const id = tag?.id;
    let valueToCopy: string;
    // Custom fields are the only items whose id corresponds to an actual
    // attribute definition; everything else (built-in merge tags) should
    // always be copied as a plain {{id}} placeholder. Using name.includes(' ')
    // here was unreliable (e.g. "City"/"State" have no space but are not
    // custom fields), causing the wrong/stale value to remain on the clipboard.
    if (this.getAttributeById(id)) {
      valueToCopy = this.buildCustomFieldTemplate(id, name);
    } else {
      valueToCopy = `{{${id}}}`;
    }

    if (!valueToCopy) {
      return;
    }

    // Copy to clipboard
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(valueToCopy).catch((err) => {
        console.error('Failed to copy to clipboard:', err);
        window.alert('Failed to copy to clipboard. Please copy manually.');
      });
    } else {
      const temp = document.createElement('textarea');
      temp.value = valueToCopy;
      document.body.appendChild(temp);
      temp.select();
      document.execCommand('copy');
      document.body.removeChild(temp);
    }

    // Focus the editor after copying so the user can immediately paste (e.g., with Ctrl+V)
    if (this.editorInstance) {
      this.editorInstance.focus(); // <-- use instance, not class
    }
  }

  onReady(editor: ClassicEditor) {
    if (this.readOnly) {
      editor.enableReadOnlyMode('');
    }
    this.editorInstance = editor;
    editor.ui
      .getEditableElement()
      .parentElement.insertBefore(editor.ui.view.toolbar.element, editor.ui.getEditableElement());

    if (this.getFocusWhenStart) {
      editor.focus();
    }
    editor.ui.focusTracker.on('change:isFocused', (evt, name, value) => {
      this.focusChanged(value);
    });
  }
  onChange(event) {
    const data = event.editor.getData();
    if (this.form) {
      this.form.get(this.field).setValue(data);
      this.form.get(this.field).markAsDirty();
      this.textChanged.emit(data);
    }
  }

  hasErrors() {
    return this.form.get(this.field).touched && this.form.get(this.field).errors;
  }

  capitalize(str) {
    if (typeof str !== 'string') return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  focusChanged(isFocused: boolean) {
    this.isFocused = isFocused;
    if (!this.isFocused && this.form) {
      this.form.get(this.field).touched = true;
    }
  }

  ngOnDestroy(): void {
    if (this.formValueChanges) this.formValueChanges.unsubscribe();
  }
}
export interface MergeTagGroup {
  name: string;
  items: ItemPickerModel[];
}
